import { Transaction } from '../../../../core/models/transaction';
import { TransactionMove, transactionMoves, transactionStyle, TransactionTone } from '../../models/transaction-style';

/** Flat grid row so search, filters, sorting and export work on plain fields. */
export interface TransactionRow {
  transaction: Transaction;
  id?: number;
  createdDate: Date | null;
  typeLabel: string;
  tone: TransactionTone;
  barcode: string;
  products: string;
  lots: string;
  reason: string;
  details: string;
  user: string;
  originalQty?: number;
  adjustedQty?: number;
  /** adjustedQty - originalQty, when both are set and differ. */
  delta?: number;
  moves: TransactionMove[];
  /** Show the Change column as a move rather than a quantity. */
  isMove: boolean;
  /** Whole from/to path, e.g. "6th Ave, 6TH 3". */
  moveFrom: string;
  moveTo: string;
  /** Plain-text Change column, for sort, filter and export; blank when nothing changed. */
  changeText: string;
}

export function toTransactionRow(t: Transaction): TransactionRow {
  const { shortLabel, tone } = transactionStyle(t.transactionType);
  const lots = t.tranDetailLot ?? [];
  const moves = transactionMoves(t);
  const hasQty = t.originalQty != null && t.adjustedQty != null;
  const delta = hasQty && t.adjustedQty !== t.originalQty ? t.adjustedQty - t.originalQty : undefined;
  const moveFrom = joinPath(t.originalWarehouse, t.originalRoom, t.originalLocation);
  const moveTo = joinPath(t.destinationWarehouse, t.destinationRoom, t.destinationLocation);
  const isMove = moves.length > 0 && delta == null;

  return {
    transaction: t,
    id: t.id,
    createdDate: t.createdDate ? new Date(t.createdDate) : null,
    typeLabel: shortLabel,
    tone,
    barcode: t.humBarcode ?? '',
    products: unique(lots.map(x => x.item)).join(', '),
    lots: unique(lots.map(x => x.lot)).join(', '),
    reason: t.reasonCode ?? '',
    details: t.details ?? '',
    user: t.createdBy ?? '',
    originalQty: t.originalQty,
    adjustedQty: t.adjustedQty,
    delta,
    moves,
    isMove,
    moveFrom,
    moveTo,
    changeText: isMove
      ? `${moveFrom} → ${moveTo}`
      : delta != null ? `${t.originalQty} → ${t.adjustedQty}` : ''
  };
}

function joinPath(...parts: (string | undefined)[]): string {
  return parts.filter(Boolean).join(', ') || '—';
}

function unique(values: (string | undefined)[]): string[] {
  return [...new Set(values.filter(Boolean))];
}
